import * as THREE from "three";
import { QUALITY, type QualityPreset } from "../config";

export type Lamp = {
  pos: THREE.Vector3;
  color: THREE.Color;
  intensity: number;
  distance: number;
  kind: "sodium" | "led" | "red";
};

export class LightRig {
  moon: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  points: THREE.PointLight[] = [];
  lamps: Lamp[] = [];
  private strobe = 0;
  private reds: THREE.PointLight[] = [];

  constructor(scene: THREE.Scene, quality: QualityPreset) {
    this.hemi = new THREE.HemisphereLight(0x6a7c96, 0x1c120c, 0.2);
    scene.add(this.hemi);

    this.moon = new THREE.DirectionalLight(0xb7c8dc, 0.38);
    this.moon.position.set(-40, 60, 18);
    this.moon.castShadow = QUALITY[quality].shadows;
    this.moon.shadow.mapSize.set(QUALITY[quality].shadowSize, QUALITY[quality].shadowSize);
    this.moon.shadow.bias = -0.00025;
    this.moon.shadow.normalBias = 0.03;
    const cam = this.moon.shadow.camera as THREE.OrthographicCamera;
    cam.left = -48;
    cam.right = 48;
    cam.top = 48;
    cam.bottom = -48;
    cam.near = 1;
    cam.far = 160;
    scene.add(this.moon);
    scene.add(this.moon.target);

    const n = QUALITY[quality].pointLights;
    for (let i = 0; i < n; i++) {
      const p = new THREE.PointLight(0xffb347, 0, 18, 1.7);
      p.castShadow = false;
      scene.add(p);
      this.points.push(p);
    }
  }

  addLamp(x: number, y: number, z: number, kind: Lamp["kind"] = "sodium") {
    const color =
      kind === "sodium" ? new THREE.Color(0xffb347) : kind === "led" ? new THREE.Color(0x7ec8e3) : new THREE.Color(0xff2a2a);
    const intensity = kind === "red" ? 8 : kind === "led" ? 16 : 24;
    const distance = kind === "red" ? 12 : 26;
    this.lamps.push({ pos: new THREE.Vector3(x, y, z), color, intensity, distance, kind });
  }

  setQuality(q: QualityPreset, scene: THREE.Scene) {
    const spec = QUALITY[q];
    this.moon.castShadow = spec.shadows;
    this.moon.shadow.mapSize.set(spec.shadowSize, spec.shadowSize);
    while (this.points.length < spec.pointLights) {
      const p = new THREE.PointLight(0xffb347, 0, 18, 1.7);
      scene.add(p);
      this.points.push(p);
    }
    while (this.points.length > spec.pointLights) {
      const p = this.points.pop()!;
      scene.remove(p);
      p.dispose();
    }
  }

  update(player: THREE.Vector3, dt: number) {
    this.strobe += dt;
    this.moon.target.position.set(player.x, 0, player.z);
    this.moon.position.set(player.x - 35, 55, player.z + 20);
    this.moon.target.updateMatrixWorld();

    const scored = this.lamps
      .map((l) => ({ l, d: l.pos.distanceToSquared(player) }))
      .sort((a, b) => a.d - b.d);

    for (let i = 0; i < this.points.length; i++) {
      const p = this.points[i];
      const slot = scored[i];
      if (!slot) {
        p.intensity = 0;
        continue;
      }
      const lamp = slot.l;
      p.position.copy(lamp.pos);
      p.color.copy(lamp.color);
      p.distance = lamp.distance;
      let inten = lamp.intensity;
      if (lamp.kind === "red") {
        inten *= 0.25 + 0.75 * (Math.sin(this.strobe * 8) > 0.2 ? 1 : 0.05);
      }
      p.intensity = inten;
    }
  }
}
